import React, { useState, useEffect, useCallback } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Badge } from '../ui/badge';
import { 
  LayoutDashboard, FileText, AlertTriangle, Calendar, GraduationCap, 
  Info, ExternalLink, Users, Star, ClipboardCheck, Zap, MessageSquare
} from 'lucide-react';
import ISODashboardCharts from './ISODashboardCharts';
import SurveysList from '../SurveysList';
import DocumentRepository from './DocumentRepository';
import NCRCAPAManagement from './NCRCAPAManagement';
import AuditManagement from './AuditManagement';
import TrainingRecords from './TrainingRecords';
import EmployeeISOInfo from './EmployeeISOInfo';
import EmployeeRatings from './EmployeeRatings';
import InstallationChecklists from './InstallationChecklists';
import ElectricalTestReports from './ElectricalTestReports';
import ISOStandardSelector, { getStandard, ISO_STANDARDS } from './ISOStandardSelector';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useShortcutTarget } from '../../contexts/ShortcutsContext';
import { ISO_TAB_META, autoColor, metaLabel } from '../../constants/shortcutTargets';
import usePermissions from '../../hooks/usePermissions';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const ISO9001Page = ({ token, employees = [], projects = [], currentUser, navContext }) => {
  const { t } = useTranslation();
  const { can, canAny } = usePermissions();
  // Which ISO tabs exist for this user is decided by the permissions matrix.
  const tabs = {
    dashboard: can('iso_dashboard.view'),
    documents: can('iso_documents.view'),
    checklists: can('iso_checklists.view'),
    'test-reports': can('iso_test_reports.view'),
    'customer-surveys': can('customer_surveys.view'),
    'ncr-capa': canAny('iso_ncrs.view', 'iso_capas.view'),
    audits: can('iso_audits.view'),
    training: can('iso_training.view'),
    employees: can('employees.view'),
    ratings: can('iso_ratings.view'),
  };
  const firstTab = Object.keys(tabs).find((k) => tabs[k]) || 'documents';
  const [activeTab, setActiveTab] = useState(firstTab);

  // Handle cross-module navigation to audit / employees
  React.useEffect(() => {
    if (navContext?.auditId) {
      setActiveTab('audits');
    } else if (navContext?.isoSubTab) {
      setActiveTab(navContext.isoSubTab);
    }
  }, [navContext]);

  const isoMeta = ISO_TAB_META[activeTab] || { icon: 'iso', perms: [] };
  useShortcutTarget({
    tab: 'iso9001',
    context: { isoSubTab: activeTab },
    label: metaLabel(isoMeta, t) || activeTab,
    subtitle: t('nav.iso'),
    icon: isoMeta.icon,
    color: autoColor(`iso-${activeTab}`),
    perms: isoMeta.perms,
  }, 1);

  const [selectedStandard, setSelectedStandard] = useState('iso9001');
  const [isLoading, setIsLoading] = useState(true);
  
  // Data state
  const [dashboardData, setDashboardData] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [ncrs, setNCRs] = useState([]);
  const [capas, setCAPAs] = useState([]);
  const [audits, setAudits] = useState([]);
  const [training, setTraining] = useState([]);
  const [docCategories, setDocCategories] = useState([]);
  
  // Get current standard info
  const currentStandard = getStandard(selectedStandard);

  // Fetch dashboard data
  const fetchDashboard = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/iso/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setDashboardData(data);
      }
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
    }
  }, [token]);

  // Fetch documents
  const fetchDocuments = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/iso/documents`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setDocuments(data);
      }
    } catch (error) {
      console.error('Failed to fetch documents:', error);
    }
  }, [token]);

  // Fetch NCRs
  const fetchNCRs = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/iso/ncrs`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setNCRs(data);
      }
    } catch (error) {
      console.error('Failed to fetch NCRs:', error);
    }
  }, [token]);

  // Fetch CAPAs
  const fetchCAPAs = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/iso/capas`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setCAPAs(data);
      }
    } catch (error) {
      console.error('Failed to fetch CAPAs:', error);
    }
  }, [token]);

  // Fetch audits
  const fetchAudits = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/iso/audits`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setAudits(data);
      }
    } catch (error) {
      console.error('Failed to fetch audits:', error);
    }
  }, [token]);

  // Fetch training
  const fetchTraining = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/iso/training`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setTraining(data);
      }
    } catch (error) {
      console.error('Failed to fetch training:', error);
    }
  }, [token]);

  // Fetch document categories
  const fetchDocCategories = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/settings/document-categories`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setDocCategories(data.categories || []);
      }
    } catch (error) {
      console.error('Failed to fetch doc categories:', error);
    }
  }, [token]);

  // Fetch all data
  const fetchAllData = useCallback(async () => {
    setIsLoading(true);
    await Promise.all([
      fetchDashboard(),
      fetchDocuments(),
      fetchNCRs(),
      fetchCAPAs(),
      fetchAudits(),
      fetchTraining(),
      fetchDocCategories(),
    ]);
    setIsLoading(false);
  }, [fetchDashboard, fetchDocuments, fetchNCRs, fetchCAPAs, fetchAudits, fetchTraining, fetchDocCategories]);

  // Initial load
  useEffect(() => {
    let mounted = true;
    
    const loadData = async () => {
      if (token && mounted) {
        setIsLoading(true);
        await Promise.all([
          fetchDashboard(),
          fetchDocuments(),
          fetchNCRs(),
          fetchCAPAs(),
          fetchAudits(),
          fetchTraining(),
          fetchDocCategories(),
        ]);
        if (mounted) {
          setIsLoading(false);
        }
      }
    };
    
    loadData();
    
    return () => {
      mounted = false;
    };
  }, [token, fetchDashboard, fetchDocuments, fetchNCRs, fetchCAPAs, fetchAudits, fetchTraining, fetchDocCategories]);

  // Refresh handlers
  const handleRefreshDocuments = () => {
    fetchDocuments();
    fetchDashboard();
  };

  const handleRefreshNCRsCAPAs = () => {
    fetchNCRs();
    fetchCAPAs();
    fetchDashboard();
  };

  const handleRefreshAudits = () => {
    fetchAudits();
    fetchDashboard();
  };

  const handleRefreshTraining = () => {
    fetchTraining();
    fetchDashboard();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">{t('iso9001.management.loading')}</div>
      </div>
    );
  }

  const StandardIcon = currentStandard.icon;

  return (
    <div className="space-y-6" data-testid="iso9001-page">
      {/* Header with Standard Selector */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <StandardIcon className={`h-7 w-7 ${currentStandard.color}`} />
            <h1 className="text-2xl font-bold text-gray-900">{t('iso9001.management.title')}</h1>
          </div>
          <p className="text-gray-500 text-sm mt-1">
            {t('iso9001.management.subtitle')}
          </p>
        </div>
        
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <ISOStandardSelector 
            selectedStandard={selectedStandard}
            onStandardChange={setSelectedStandard}
            showDisabled={true}
          />
        </div>
      </div>
      
      {/* Info banner for future standards */}
      {!currentStandard.enabled && (
        <div className={`p-4 rounded-lg border ${currentStandard.bgColor} border-${currentStandard.color.split('-')[1]}-200`}>
          <div className="flex items-start gap-3">
            <Info className={`h-5 w-5 ${currentStandard.color} flex-shrink-0 mt-0.5`} />
            <div>
              <p className={`font-medium ${currentStandard.color}`}>
                {currentStandard.fullName} - Coming Soon
              </p>
              <p className="text-sm text-gray-600 mt-1">
                {currentStandard.description}. This standard will share the same document control, 
                NCR/CAPA, audit, and training infrastructure with ISO 9001, allowing for integrated 
                management system (IMS) capabilities.
              </p>
            </div>
          </div>
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="flex flex-wrap h-auto gap-1 p-1 w-full max-w-6xl">
          {tabs.dashboard && (
            <TabsTrigger value="dashboard" className="flex items-center gap-2" data-testid="iso-dashboard-tab">
              <LayoutDashboard className="h-4 w-4" />
              <span className="hidden sm:inline">{t('iso9001.dashboard')}</span>
            </TabsTrigger>
          )}
          {tabs.documents && (
            <TabsTrigger value="documents" className="flex items-center gap-2" data-testid="tab-documents">
              <FileText className="h-4 w-4" />
              <span className="hidden sm:inline">{t('iso9001.documents')}</span>
            </TabsTrigger>
          )}
          {tabs.checklists && (
            <TabsTrigger value="checklists" className="flex items-center gap-2" data-testid="iso-checklists-tab">
              <ClipboardCheck className="h-4 w-4" />
              <span className="hidden sm:inline">{t('iso9001.checklists')}</span>
            </TabsTrigger>
          )}
          {tabs['test-reports'] && (
            <TabsTrigger value="test-reports" className="flex items-center gap-2" data-testid="iso-test-reports-tab">
              <Zap className="h-4 w-4" />
              <span className="hidden sm:inline">{t('iso9001.testReports')}</span>
            </TabsTrigger>
          )}
          {tabs['customer-surveys'] && (
            <TabsTrigger value="customer-surveys" className="flex items-center gap-2" data-testid="iso-surveys-tab">
              <MessageSquare className="h-4 w-4" />
              <span className="hidden sm:inline">{t('iso9001.customerSurveys')}</span>
            </TabsTrigger>
          )}
          {tabs['ncr-capa'] && (
            <TabsTrigger value="ncr-capa" className="flex items-center gap-2" data-testid="tab-ncr-capa">
              <AlertTriangle className="h-4 w-4" />
              <span className="hidden sm:inline">{t('iso9001.ncrCapa')}</span>
            </TabsTrigger>
          )}
          {tabs.audits && (
            <TabsTrigger value="audits" className="flex items-center gap-2" data-testid="tab-audits">
              <Calendar className="h-4 w-4" />
              <span className="hidden sm:inline">{t('iso9001.audits')}</span>
            </TabsTrigger>
          )}
          {tabs.training && (
            <TabsTrigger value="training" className="flex items-center gap-2" data-testid="tab-training">
              <GraduationCap className="h-4 w-4" />
              <span className="hidden sm:inline">{t('iso9001.training')}</span>
            </TabsTrigger>
          )}
          {tabs.employees && (
            <TabsTrigger value="employees" className="flex items-center gap-2" data-testid="iso-employees-tab">
              <Users className="h-4 w-4" />
              <span className="hidden sm:inline">{t('iso9001.employees')}</span>
            </TabsTrigger>
          )}
          {tabs.ratings && (
            <TabsTrigger value="ratings" className="flex items-center gap-2" data-testid="iso-ratings-tab">
              <Star className="h-4 w-4" />
              <span className="hidden sm:inline">{t('iso9001.ratings')}</span>
            </TabsTrigger>
          )}
        </TabsList>

        {tabs.dashboard && (
          <TabsContent value="dashboard">
            <ISODashboardCharts
              dashboardData={dashboardData}
              token={token}
              onNavigate={setActiveTab}
            />
          </TabsContent>
        )}

        {tabs.documents && (
        <TabsContent value="documents">
          <DocumentRepository
            documents={documents}
            employees={employees}
            projects={projects}
            onRefresh={handleRefreshDocuments}
            token={token}
            docCategories={docCategories}
            currentUser={currentUser}
          />
        </TabsContent>
        )}

        {tabs.checklists && (
        <TabsContent value="checklists">
          <InstallationChecklists
            token={token}
            projects={projects}
            employees={employees}
            currentUser={currentUser}
          />
        </TabsContent>
        )}

        {tabs['test-reports'] && (
        <TabsContent value="test-reports">
          <ElectricalTestReports
            token={token}
            projects={projects}
            currentUser={currentUser}
          />
        </TabsContent>
        )}

        {tabs['customer-surveys'] && (
          <TabsContent value="customer-surveys">
            <SurveysList projects={projects} />
          </TabsContent>
        )}

        {tabs['ncr-capa'] && (
            <TabsContent value="ncr-capa">
              <NCRCAPAManagement
                ncrs={ncrs}
                capas={capas}
                employees={employees}
                projects={projects}
                onRefresh={handleRefreshNCRsCAPAs}
                token={token}
              />
            </TabsContent>
        )}
        {tabs.audits && (
            <TabsContent value="audits">
              <AuditManagement
                audits={audits}
                employees={employees}
                projects={projects}
                documents={documents}
                onRefresh={handleRefreshAudits}
                token={token}
                navContext={navContext}
              />
            </TabsContent>
        )}
        {tabs.training && (
            <TabsContent value="training">
              <TrainingRecords
                training={training}
                employees={employees}
                projects={projects}
                documents={documents}
                onRefresh={handleRefreshTraining}
                token={token}
              />
            </TabsContent>
        )}
        {tabs.employees && (
            <TabsContent value="employees">
              <EmployeeISOInfo
                employees={employees}
                training={training}
                audits={audits}
                ncrs={ncrs}
                documents={documents}
                token={token}
                initialSearch={navContext?.employeeSearch}
              />
            </TabsContent>
        )}
        {tabs.ratings && (
            <TabsContent value="ratings">
              <EmployeeRatings
                token={token}
                employees={employees}
                projects={projects}
                currentUser={currentUser}
              />
            </TabsContent>
        )}
      </Tabs>
    </div>
  );
};

export default ISO9001Page;
